import { createLoader } from '@meteor/data-hooks';
import { useRef, useState } from 'react';
import { autoPaginate, withManagementSDK } from '../../management';
import { withAuth } from '../../user';
import { agentClient } from '../client';

export let agentsLoader = createLoader({
  name: 'agents',
  hash: i => i.projectId,
  fetch: (i: { projectId: string }) => {
    return withManagementSDK(i, sdk =>
      autoPaginate(pag => sdk.projects.agents.list(i.projectId, pag))
    );
  },
  mutators: {
    create: (i: { name: string; slug: string }, { input }) =>
      withManagementSDK(input, sdk =>
        sdk.projects.agents.create(input.projectId, { ...input, ...i })
      )
  }
});

export let useAgents = (projectId: string | null | undefined) => {
  let agents = agentsLoader.use(projectId ? { projectId } : null);

  return {
    ...agents,
    createMutator: agents.useMutator('create')
  };
};

export let agentLoader = createLoader({
  name: 'agent',
  hash: i => i.agentId + i.instanceId,
  parents: [agentsLoader],
  fetch: (i: { agentId: string; instanceId: string }) =>
    withAuth(() =>
      agentClient.agent.get({
        agentId: i.agentId,
        instanceId: i.instanceId
      })
    ),

  mutators: {
    update: (
      i: {
        name?: string;
      },
      { input, output }
    ) =>
      withManagementSDK(input, sdk =>
        sdk.projects.agents.update(output.projectId, input.agentId, {
          ...input,
          ...i
        })
      ),

    delete: (_, { input, output }) =>
      withManagementSDK(input, sdk =>
        sdk.projects.agents.delete(output.projectId, input.agentId)
      )
  }
});

export let useAgent = (
  instanceId: string | null | undefined,
  agentId: string | null | undefined
) => {
  let agent = agentLoader.use(agentId && instanceId ? { agentId, instanceId } : null);

  return {
    ...agent,
    updateMutator: agent.useMutator('update'),
    deleteMutator: agent.useMutator('delete')
  };
};

export let agentReleasesLoader = createLoader({
  name: 'agent_releases',
  hash: i => i.agentId + i.projectId + i.cursor,
  fetch: (i: { agentId: string; projectId: string; cursor?: string }) =>
    withManagementSDK(i, sdk =>
      sdk.projects.agents.releases.list(i.projectId, i.agentId, {
        limit: 50,
        order: 'desc'
      })
    ),
  mutators: {}
});

export let useAgentReleases = (
  projectId: string | null | undefined,
  agentId: string | null | undefined
) => {
  let [cursor, setCursor] = useState<string>();
  let releases = agentReleasesLoader.use(
    agentId && projectId ? { agentId, projectId, cursor } : null
  );

  let dataRef = useRef(releases.data);
  dataRef.current = releases.data;

  return {
    ...releases,
    next: () => {
      let c = dataRef.current?.pagination.afterCursor;
      if (c) setCursor(c);
    },
    previous: () => {
      let c = dataRef.current?.pagination.beforeCursor;
      if (c) setCursor(c);
    }
  };
};

export let mostRecentAgentUpdateLoader = createLoader({
  name: 'agent_releases',
  hash: i => i.agentId + i.instanceId,
  fetch: (i: { agentId: string; instanceId: string }) =>
    withAuth(() => agentClient.agent.release.getMostRecentUpdate(i)),
  mutators: {}
});

export let useMostRecentAgentUpdate = (
  instanceId: string | null | undefined,
  agentId: string | null | undefined
) => {
  let update = mostRecentAgentUpdateLoader.use(
    agentId && instanceId ? { agentId, instanceId } : null
  );

  return update;
};
